import React from 'react';
import { max } from 'lodash';
import {
    Angebotsposition,
    Kalkulationsposition,
    DeleteOfferDocument,
    DeleteInvoiceDocument,
    ListDocumentsDocument,
    Rechnungsposition,
    ListCalculationPositionsDocument,
    Subprojekt,
} from '~/gql/ucpw/graphql';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { CreatePdfResponse } from '~/gql/ucc/graphql';
import { tables } from '~/pages/projects/meta/data/finance.schema';
import { ProjectIds } from '~/pages/projects/types';
import { usePdf } from '~/pages/projects/hooks/usePdf';
import { VALID_OFFER_STATUS_KEY } from '~/pages/projects/ui/Finance/constants';
import { isPxConnectSource } from '~/meta/data/px-connect';
import { useDeleteWithConfirmation, usePermission, useSnippetId } from '~/hooks';
import { useEditable } from './useEditable';
import { client } from '~/apollo';
import { withRenderers } from '~/utils';
import log from '~/log';

export const Modals = {
    EditFreePosition: 'EditFreePosition',
    SelectGeneralAggrements: 'SelectGeneralAggrements',
    EditGeneralAggrementPosition: 'EditGeneralAggrementPosition',
    Workdescription: 'Workdescription',
};

export type FinancePosition = Kalkulationsposition | Angebotsposition | Rechnungsposition;

export const getCalculations = (positions = []) => {
    const generalAgreementPositionIds = positions
        .map((p: any) => p.rahmenvertragspositionId)
        .filter(Boolean);
    const highestPosition = max(positions.map((p: any) => parseInt(p.lfdNr, 10)));

    return { generalAgreementPositionIds, highestPosition };
};

export const getOriginalAndModalFromPosition = (position: FinancePosition) => {
    const original = {
        ...position,
        preisProEinheit: position.einzelpreis,
        leistung: { einheit: position.einheit },
        residentialUnitId: position.subprojektWohneinheitId,
    };
    const modal = position.rahmenvertragspositionId
        ? Modals.EditGeneralAggrementPosition
        : Modals.EditFreePosition;

    log.trace('getOriginalAndModalFromPosition', { position, original, modal });

    return { original, modal };
};

export const calculateTotalPriceNet = (positions = []) => {
    const totalPriceNet = positions.reduce(
        (acc: number, pos: FinancePosition) => acc + pos?.menge * pos?.einzelpreis,
        0
    );
    return totalPriceNet;
};

type Props = {
    refetchQueries?: [];
    totalPriceNet?: number;
    pageHash?: any;
    calculationPositions?: any[];
    send?: (args?: any) => void;
    viewer?: any;
    permissions?: any;
    setModal?: (modal: string, props?: Record<string, any>) => void;
} & ProjectIds;

export const useFinance = ({
    projectId,
    subprojectId,
    calculationPositions,
    setModal,
    send,
    viewer,
}: Props) => {
    log.debug('useFinance', { projectId, subprojectId, calculationPositions, viewer });
    const { typSnippet, typSnippetId } = useSnippetId({
        name: 'Angebot',
        category: 'Dokument',
        property: 'Typ',
    });
    // Korrespondenz-Typ/-Subtyp für den Angebots-Flow GEZIELT nachschlagen, nicht aus einer
    // Sammelliste filtern: `listSnippet` ohne `limit` liefert serverseitig nur 20 Zeilen
    // (`satanizeLimit`, api/src/db.ts), die Kategorie „Korrespondenz" hat aber mehr. Auf
    // staging nachgemessen: 22 Zeilen, abgeschnitten werden „PXSubtyp/SB/Schadenbericht"
    // und „Typ/PXC/PX Connect Nachricht" — der PXC-*Typ* wäre in einer Sammelliste also
    // nicht auffindbar (der Subtyp „PXSubtyp/AN/Angebot" lag knapp innerhalb).
    // Muster wie in usePxConnectKorrespondenz: Filter serverseitig auf Kategorie +
    // Eigenschaft + Name, damit kein Limit greifen kann.
    const { typSnippetId: gdvTypSnippetId } = useSnippetId({
        name: 'GDV-Nachricht',
        category: 'Korrespondenz',
        property: 'Typ',
    });
    const { typSnippetId: pxcTypSnippetId } = useSnippetId({
        name: 'PX Connect Nachricht',
        category: 'Korrespondenz',
        property: 'Typ',
    });
    const { typSnippetId: gdvAngebotSubtypId } = useSnippetId({
        name: 'Angebot',
        category: 'Korrespondenz',
        property: 'Subtyp',
    });
    const { typSnippetId: pxcAngebotSubtypId } = useSnippetId({
        name: 'Angebot',
        category: 'Korrespondenz',
        property: 'PXSubtyp',
    });
    const navigate = useNavigate();
    const toast = useToast();
    const {
        canView: canViewInvoice,
        canEdit: canEditInvoice,
        canDelete: canDeleteInvoice,
        canCreate: canCreateInvoice,
    } = usePermission('finance.invoice');
    const {
        canView: canViewOffer,
        canEdit: canEditOffer,
        canDelete: canDeleteOffer,
    } = usePermission('finance.offer');
    const downloadInvoiceEnabled = viewer.hasPermission('additional.downloadInvoice', 'enabled');

    const pdf = usePdf({ projectId, subprojectId });
    // Herkunft des Subprojekts (siehe CONTEXT.md): entscheidet Beschriftung UND Kanal des
    // Angebots-Menüpunkts. „Servicepool" ist das Anwenderwort für `gothaer`; „GDV" benennt
    // nur das Nachrichtenformat, nie den Kanal.
    const isPxConnect = isPxConnectSource((pdf?.subproject as Subprojekt)?.metadata);
    const { updateEditable } = useEditable();

    // ===============================================
    // LIST
    const context = { clientName: 'ucpw' };
    const [offersDeleteMutation] = useMutation(DeleteOfferDocument, { context });
    const [invoicesDeleteMutation] = useMutation(DeleteInvoiceDocument, { context });

    const refetchQueries = [
        // external
        {
            query: ListCalculationPositionsDocument,
            context,
            variables: {
                filter: {
                    subprojektId: subprojectId,
                },
                limit: 1000,
            },
        },
    ];

    // ===============================================
    // DELETE
    // ===============================================

    const { deleteWithConfirmation: deleteOfferWithConfirmation } = useDeleteWithConfirmation({
        deleteMutation: offersDeleteMutation,
        refetchQueries,
    });
    const { deleteWithConfirmation: deleteInvoiceWithConfirmation } = useDeleteWithConfirmation({
        deleteMutation: invoicesDeleteMutation,
        refetchQueries,
    });

    // ===============================================
    // TABLE
    // ===============================================

    const fetchRelatedDocuments = async (filter: any) => {
        const response = await client.query({
            query: ListDocumentsDocument,
            context: { clientName: 'ucpw' },
            fetchPolicy: 'network-only',
            variables: { limit: 50, filter },
        });
        const documents = response?.data?.items?.items || [];
        return documents;
    };

    const deleteEnabled = React.useCallback(
        (row: any) => {
            const { statusSnippet, __typename } = row?.original || {};

            if (['gültig', 'D', 'S'].includes(statusSnippet?.name)) {
                return false;
            }

            if (__typename === 'Angebot' && canDeleteOffer) {
                return canDeleteOffer;
            }

            if (__typename === 'Rechnung' && canDeleteInvoice) {
                return canDeleteInvoice;
            }

            return false;
        },
        [canDeleteInvoice, canDeleteOffer]
    );

    const controls = React.useMemo(
        () =>
            [
                {
                    title: canViewOffer && !canEditOffer ? 'Ansehen' : 'Bearbeiten',
                    props: {
                        onClick: (row: any) => {
                            const { id } = row?.original || {};
                            const type = 'offer';
                            send?.({ type: `EDIT_${type.toUpperCase()}`, id });
                            updateEditable(id, type);
                        },
                    },
                    enabled: (row: any) =>
                        row?.original?.__typename === 'Angebot' && (canEditOffer || canViewOffer),
                },
                {
                    title: canViewInvoice && !canEditInvoice ? 'Ansehen' : 'Bearbeiten',
                    props: {
                        onClick: (row: any) => {
                            const { id } = row?.original || {};
                            const type = 'invoice';
                            send?.({ type: `EDIT_${type.toUpperCase()}`, id });
                            updateEditable(id, type);
                        },
                    },
                    enabled: (row: any) =>
                        row?.original?.__typename === 'Rechnung' &&
                        (canEditInvoice || canViewInvoice),
                },
                {
                    title: 'Download',
                    props: {
                        onClick: async (row: any) => {
                            const { id: angebotId, statusSnippet } = row?.original || {};
                            const dokument = await fetchRelatedDocuments({
                                angebotId,
                                deletedExists: false,
                            });
                            return pdf.offer({
                                ...row?.original,
                                dokument,
                                ...(statusSnippet?.name === 'in Bearbeitung' && {
                                    angebotspositionen: calculationPositions,
                                }),
                            });
                        },
                    },
                    enabled: (row: any) =>
                        row?.original?.__typename === 'Angebot' && (canEditOffer || canViewOffer),
                },
                {
                    title: 'Download',
                    props: {
                        onClick: async (row: any) => {
                            const { id: rechnungId, statusSnippet } = row?.original || {};
                            const dokument = await fetchRelatedDocuments({
                                rechnungId,
                                deletedExists: false,
                            });
                            return pdf.invoice({
                                ...row?.original,
                                dokument,
                                ...(statusSnippet?.name === 'Offen' &&
                                    !row?.original?.rechnungspositionen?.length && {
                                        rechnungspositionen: calculationPositions,
                                    }),
                            });
                        },
                    },
                    enabled: (row: any) =>
                        row?.original?.__typename === 'Rechnung' && downloadInvoiceEnabled,
                },
                {
                    title: isPxConnect
                        ? 'PX Connect Angebot vorbereiten'
                        : 'Servicepool Angebot vorbereiten',
                    props: {
                        onClick: async (row: any) => {
                            toast({
                                title: 'Das PDF wird erzeugt',
                                status: 'info',
                                duration: 5000,
                                isClosable: true,
                                description: 'Sie werden im Anschluss automatisch weitergeleitet',
                            });
                            const response = await pdf.offer(row.original, 'get');
                            console.log('useFinance.getOfferPdf', response);

                            if (!(response as CreatePdfResponse)?.error) {
                                const pfad = (response as CreatePdfResponse)?.key || '';
                                const name = pfad.split('/').pop() || '';
                                // Typ/Subtyp müssen zur Herkunft passen, sonst bleibt die
                                // Vorbelegung im Korrespondenz-Modal leer: der GDV-Typ wird im
                                // PXC-Kontext weggefiltert (correspondence.schema.ts) und die
                                // PXC-Subtypen liegen unter der eigenen eigenschaft „PXSubtyp".
                                // Der Subtyp ist nicht kosmetisch — der Rückkanal routet über
                                // dessen Kürzel (`AN` → `offer.document`,
                                // service-request-worker.ts). Bleibt er leer, verlässt das
                                // Angebot das Portal nie, ohne Fehlermeldung.
                                const state = {
                                    // `useSnippetId` liefert 0, wenn nichts gefunden wurde —
                                    // eine 0 im Formular wäre eine erfundene Snippet-Id.
                                    typSnippetId:
                                        (isPxConnect ? pxcTypSnippetId : gdvTypSnippetId) ||
                                        undefined,
                                    subtypSnippetId:
                                        (isPxConnect ? pxcAngebotSubtypId : gdvAngebotSubtypId) ||
                                        undefined,
                                    korrespondenzDokument: [
                                        {
                                            dokument: {
                                                pfad,
                                                name,
                                                typSnippet: { value: typSnippetId },
                                                angebotId: row.original.id,
                                                uploadDatum: new Date(),
                                            },
                                        },
                                    ],
                                    __typename: 'Dokument',
                                };
                                console.log('useFinance.createCorrespondence', state);
                                navigate(`/projekte/${projectId}/${subprojectId}/details`, {
                                    state,
                                });
                            } else {
                                toast({
                                    title: 'Fehler beim Erstellen des Angebots',
                                    status: 'error',
                                });
                            }
                        },
                    },
                    enabled: (row: any) =>
                        row?.original?.__typename === 'Angebot' &&
                        row?.original?.statusSnippet?.name === 'gültig' &&
                        (pdf?.subproject as Subprojekt)?.metadata?.includes?.('serviceRequestId'),
                },
                {
                    title: 'Rechnung erzeugen',
                    props: {
                        onClick: (row: any) => {
                            const { id } = row?.original || {};
                            send?.({ type: 'NEW_INVOICE', fromOfferId: id });
                            updateEditable('new', 'invoice', id);
                        },
                    },
                    enabled: (row: any) =>
                        row?.original?.__typename === 'Angebot' &&
                        row?.original?.statusSnippet?.kuerzel === VALID_OFFER_STATUS_KEY &&
                        canCreateInvoice,
                },
                {
                    title: 'Arbeitsbeschreibung erzeugen',
                    props: {
                        onClick: (row: any) => setModal?.(Modals.Workdescription, row?.original),
                    },
                    enabled: (row: any) =>
                        row?.original?.__typename === 'Angebot' &&
                        ['Vorarbeiten', 'Nacharbeiten'].includes(
                            (pdf?.subproject as Subprojekt)?.projekttyp?.name || ''
                        ),
                },
                { title: 'divider', enabled: deleteEnabled },
                {
                    title: 'Löschen',
                    props: {
                        color: 'red.400',
                        onClick: (row: any) => {
                            const callAfter = () => send?.('GET_OFFERS_AND_INVOICES');
                            const { id, __typename } = row?.original || {};
                            if (__typename === 'Angebot') {
                                deleteOfferWithConfirmation({ id }, { callAfter });
                            }
                            if (__typename === 'Rechnung') {
                                deleteInvoiceWithConfirmation({ id }, { callAfter });
                            }
                        },
                    },
                    enabled: deleteEnabled,
                },
            ].filter(Boolean),
        [
            pdf,
            calculationPositions,
            deleteEnabled,
            canCreateInvoice,
            canEditInvoice,
            canViewInvoice,
            canEditOffer,
            canViewOffer,
            typSnippetId,
            typSnippet,
            isPxConnect,
            gdvTypSnippetId,
            pxcTypSnippetId,
            gdvAngebotSubtypId,
            pxcAngebotSubtypId,
        ]
    );

    const columns = React.useMemo(
        () => withRenderers(tables.finance.columns, controls),
        [controls]
    );
    const hiddenColumns = tables.finance.hiddenColumns;

    return {
        columns,
        hiddenColumns,
    };
};
